import { useCallback } from 'react';

import type {
  ApprovalAction,
  ApprovalData,
  BulkApprovalsData,
  ApprovalType,
  ApprovalRules,
} from 'ui-product-approvals-common';

import type {
  AppHubUserRecord,
  AppTeamRecord,
  BreezeAgentRecord,
} from '../types/CrmSearchUser';
import type { ApprovalStepInput } from 'ui-product-approvals-common/types/ApprovalData';
import { Metrics } from '../utils/metrics';
import { captureException } from '../utils/sentryUtils';
import { toApprovalData } from '../utils/approvalResponseAdapters';
import {
  useCreateApprovalMutation,
  useCreateBulkApprovalMutation,
} from '../../__generated__/chirp/com/hubspot/product/approvals/rpc/ApprovalsRpc.hooks';
import { GET_LATEST_APPROVAL_QUERY } from '../../__generated__/chirp/com/hubspot/product/approvals/rpc/ApprovalsRpc.dfc.new';
import { APPROVAL_TYPE_TO_OBJECT_TYPE_ID } from 'ui-product-approvals-common/constants/approvalTypeIds';

interface UseCreateApprovalArgs {
  approvalType: ApprovalType;
  approvalAction: ApprovalAction;
  approvableObjectId: number | undefined;
  assetName: string;
  approvalObjectIds?: number[];
  usersRecord: AppHubUserRecord;
  agentsRecord: BreezeAgentRecord;
  teamsRecord: AppTeamRecord;
}

export const useCreateApproval = ({
  approvalType,
  approvalAction,
  approvableObjectId,
  assetName,
  approvalObjectIds,
  usersRecord,
  agentsRecord,
  teamsRecord,
}: UseCreateApprovalArgs) => {
  const [createApprovalMutation] = useCreateApprovalMutation();
  const [createBulkApprovalMutation] = useCreateBulkApprovalMutation();

  const create = useCallback(
    (
      note: string,
      steps: ApprovalStepInput[],
      approvalRules: ApprovalRules,
      dueDate?: number,
      title?: string
    ): Promise<ApprovalData | BulkApprovalsData> => {
      /*
       * Currently the Bulk approval creation flow that used when approvalObjectIds are
       * passed to the ApprovalsProvider is only used in Social Posts/Broadcasts
       */
      if (approvalObjectIds) {
        const approvalRequests = approvalObjectIds.map((objectId) => ({
          approvalTypeId: APPROVAL_TYPE_TO_OBJECT_TYPE_ID[approvalType],
          approvalAction,
          approvableObjectId: objectId,
          approvalSteps: steps.map((step) =>
            step.order === undefined
              ? { actor: step.actor }
              : { actor: step.actor, order: step.order }
          ),
          approvalRules,
          title: title ?? assetName,
          requestNote: note,
          ...(dueDate && { dueDate }),
        }));
        /*
         * No cache write or refetch call — bulk approval creates span multiple approvableObjectIds
         * and caller handles the response directly
         */
        return createBulkApprovalMutation({
          variables: { approvalRequests },
        })
          .then((result) => {
            Metrics.counter('request-created').increment();

            const response = result?.data?.result;
            return {
              createdApprovals: (response?.createdApprovals ?? []).map(
                (approval) =>
                  toApprovalData(
                    approval,
                    usersRecord,
                    agentsRecord,
                    approvalType,
                    approvalAction,
                    teamsRecord
                  )
              ),
              failedApprovals: (response?.failedRequests ?? []).map((req) => ({
                approvalType,
                approvalAction: req.approvalAction as ApprovalAction,
                approvableObjectId: req.approvableObjectId,
                title: req.title,
                approverActorIds: (req.approvalSteps ?? []).map(
                  (step) => step.actor.id
                ),
                note: req.requestNote ?? '',
                approvalRules: String(req.approvalRules ?? ''),
              })),
            };
          })
          .catch((error) => {
            captureException(error, {
              extra: {
                fingerprint: 'useApprovalData-createBulk',
                approvalType,
                approvalAction,
              },
            });
            throw error;
          });
      }
      return createApprovalMutation({
        variables: {
          approvalTypeId: APPROVAL_TYPE_TO_OBJECT_TYPE_ID[approvalType],
          approvalAction,
          approvableObjectId: approvableObjectId!,
          approvalSteps: steps.map((step) =>
            step.order === undefined
              ? { actor: step.actor }
              : { actor: step.actor, order: step.order }
          ),
          approvalRules,
          title: title ?? assetName,
          requestNote: note,
          ...(dueDate && { dueDate }),
        },
        update: (cache, { data: mutationData }) => {
          const approvalData = mutationData?.result?.approval;
          if (approvalData) {
            cache.writeQuery({
              query: GET_LATEST_APPROVAL_QUERY,
              variables: {
                approvalTypeId: APPROVAL_TYPE_TO_OBJECT_TYPE_ID[approvalType],
                approvalAction,
                approvableObjectId: approvableObjectId!,
              },
              data: { result: { approval: approvalData } },
            });
          }
        },
      })
        .then((result) => {
          Metrics.counter('request-created').increment();
          const approval = result?.data?.result?.approval;
          if (approval) {
            return toApprovalData(
              approval,
              usersRecord,
              agentsRecord,
              approvalType,
              approvalAction,
              teamsRecord
            );
          } else {
            throw new Error(
              'createApproval succeeded but returned no approval data'
            );
          }
        })
        .catch((error) => {
          captureException(error, {
            extra: {
              fingerprint: 'useApprovalData-create',
              approvalType,
              approvalAction,
              approvableObjectId,
            },
          });
          throw error;
        });
    },
    [
      approvalType,
      approvalAction,
      approvableObjectId,
      assetName,
      approvalObjectIds,
      createApprovalMutation,
      createBulkApprovalMutation,
      usersRecord,
      agentsRecord,
      teamsRecord,
    ]
  );

  return create;
};
